import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { ethers } from 'ethers';
import { CHAINS, CONTRACTS, WALLET_TYPES, STORAGE_KEYS, ERROR_MESSAGES, REFRESH_INTERVALS } from '../config/constants';
import toast from 'react-hot-toast';

const WalletContext = createContext();

// Initial state
const initialState = {
  isConnected: false,
  address: null,
  chainId: null,
  provider: null,
  signer: null,
  walletType: null,
  balance: {
    native: '0',
    pfvs: '0',
  },
  isLoading: false,
  error: null,
};

// Action types
const WALLET_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CONNECT_SUCCESS: 'CONNECT_SUCCESS',
  DISCONNECT: 'DISCONNECT',
  UPDATE_BALANCE: 'UPDATE_BALANCE',
  SWITCH_CHAIN: 'SWITCH_CHAIN',
};

// Reducer
function walletReducer(state, action) {
  switch (action.type) {
    case WALLET_ACTIONS.SET_LOADING:
      return { ...state, isLoading: action.payload, error: null };
    
    case WALLET_ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, isLoading: false };
    
    case WALLET_ACTIONS.CONNECT_SUCCESS:
      return {
        ...state,
        isConnected: true,
        address: action.payload.address,
        chainId: action.payload.chainId,
        provider: action.payload.provider,
        signer: action.payload.signer,
        walletType: action.payload.walletType,
        isLoading: false,
        error: null,
      };
    
    case WALLET_ACTIONS.DISCONNECT:
      return {
        ...initialState,
      };
    
    case WALLET_ACTIONS.UPDATE_BALANCE:
      return {
        ...state,
        balance: action.payload,
      };
    
    case WALLET_ACTIONS.SWITCH_CHAIN:
      return {
        ...state,
        chainId: action.payload.chainId,
        provider: action.payload.provider,
        signer: action.payload.signer,
      };
    
    default:
      return state;
  }
}

// Wallet Provider Component
export function WalletProvider({ children }) {
  const [state, dispatch] = useReducer(walletReducer, initialState);

  // Check if wallet is already connected on mount
  useEffect(() => {
    checkConnection();
    setupEventListeners();
    
    return () => {
      cleanup();
    };
  }, []);


  const checkConnection = async () => {
    try {
      const savedWallet = localStorage.getItem(STORAGE_KEYS.WALLET_CONNECTION);
      if (savedWallet) {
        const { walletType } = JSON.parse(savedWallet);
        
                 // Check if the wallet is still available and connected
        if (walletType === WALLET_TYPES.RONIN && window.ronin) {
          try {
            const accounts = await window.ronin.request({ method: 'eth_accounts' });
            if (accounts.length > 0) {
              await connectWallet(walletType, false, true); // isReconnection = true
            }
          } catch (error) {
            console.log('Ronin wallet not connected:', error);
          }
        } else if (walletType === WALLET_TYPES.METAMASK && window.ethereum) {
          try {
            const accounts = await window.ethereum.request({ method: 'eth_accounts' });
            if (accounts.length > 0) {
              await connectWallet(walletType, false, true); // isReconnection = true
            }
          } catch (error) {
            console.log('MetaMask not connected:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error checking wallet connection:', error);
    }
  };

  const setupEventListeners = () => {
    if (window.ethereum) {
      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);
      window.ethereum.on('disconnect', handleDisconnect);
    }

    // Ronin wallet events
    if (window.ronin) {
      window.ronin.on('accountsChanged', handleAccountsChanged);
      window.ronin.on('chainChanged', handleChainChanged);
    }
  };

  const cleanup = () => {
    if (window.ethereum) {
      window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      window.ethereum.removeListener('chainChanged', handleChainChanged);
      window.ethereum.removeListener('disconnect', handleDisconnect);
    }

    if (window.ronin) {
      window.ronin.removeListener('accountsChanged', handleAccountsChanged);
      window.ronin.removeListener('chainChanged', handleChainChanged);
    }
  };

  const handleAccountsChanged = async (accounts) => {
    if (accounts.length === 0) {
      disconnect();
    } else {
      // Update with new account
      const provider = new ethers.providers.Web3Provider(
        state.walletType === WALLET_TYPES.RONIN ? window.ronin : window.ethereum
      );
      const signer = provider.getSigner();
      const address = await signer.getAddress();
      
      dispatch({
        type: WALLET_ACTIONS.CONNECT_SUCCESS,
        payload: {
          ...state,
          address,
          signer,
        },
      });
    }
  };

  const handleChainChanged = async (chainId) => {
    try {
    const numericChainId = parseInt(chainId, 16);
    
      // Create new provider with "any" network to avoid network mismatch errors
      const provider = new ethers.providers.Web3Provider(
        state.walletType === WALLET_TYPES.RONIN ? window.ronin : window.ethereum,
        "any"
      );
      const signer = provider.getSigner();
      
      dispatch({
        type: WALLET_ACTIONS.SWITCH_CHAIN,
        payload: {
          chainId: numericChainId,
          provider,
          signer,
        },
      });
      
      // Show warning for unsupported chains but don't block
      if (numericChainId !== CHAINS.RONIN.id && numericChainId !== CHAINS.BNB.id) {
        console.warn('Unsupported chain detected:', numericChainId);
        toast.warning('Connected to unsupported network. Please switch to Ronin or BNB Smart Chain for full functionality.');
      }
    } catch (error) {
      console.error('Error handling chain change:', error);
      // Don't show error toast to user, just log it
    }
  };

  const handleDisconnect = () => {
    disconnect();
  };

  const connectWallet = async (walletType, showToast = true, isReconnection = false) => {
    dispatch({ type: WALLET_ACTIONS.SET_LOADING, payload: true });

    try {
      let provider, signer, address, chainId;

      if (walletType === WALLET_TYPES.RONIN) {
        if (!window.ronin) {
          throw new Error('Ronin Wallet not found. Please install Ronin Wallet extension.');
        }

        // For reconnection, don't request accounts again
        if (!isReconnection) {
        await window.ronin.request({ method: 'eth_requestAccounts' });
        }
        
        // Create provider with more stable network detection
        provider = new ethers.providers.Web3Provider(window.ronin, "any");
        signer = provider.getSigner();
        address = await signer.getAddress();
        
        // Get current network more safely
        try {
        const network = await provider.getNetwork();
        chainId = network.chainId;
        } catch (networkError) {
          console.warn('Ronin network detection failed, trying wallet method:', networkError);
          try {
            const hexChainId = await window.ronin.request({ method: 'eth_chainId' });
            chainId = parseInt(hexChainId, 16);
          } catch (chainIdError) {
            console.error('Failed to get Ronin chain ID:', chainIdError);
            // Default to Ronin chain
            chainId = CHAINS.RONIN.id;
          }
        }

        // For reconnection, don't enforce chain - just connect and let user switch later
        // For first-time connection, don't force immediate network switch
        if (!isReconnection && chainId !== CHAINS.RONIN.id) {
          console.warn('Ronin wallet not on Ronin network, current chain:', chainId);
          // Don't throw error immediately - let auto switch handle it
        }
      } else if (walletType === WALLET_TYPES.METAMASK) {
        if (!window.ethereum) {
          throw new Error('MetaMask not found. Please install MetaMask extension.');
        }

        // For reconnection, don't request accounts again
        if (!isReconnection) {
        await window.ethereum.request({ method: 'eth_requestAccounts' });
        }
        
        // Create provider with more stable network detection
        provider = new ethers.providers.Web3Provider(window.ethereum, "any");
        signer = provider.getSigner();
        address = await signer.getAddress();
        
        // Get current network more safely
        let network;
        try {
          network = await provider.getNetwork();
        chainId = network.chainId;
        } catch (networkError) {
          // If network detection fails, try to get it from the wallet directly
          console.warn('Network detection failed, trying wallet method:', networkError);
          try {
            const hexChainId = await window.ethereum.request({ method: 'eth_chainId' });
            chainId = parseInt(hexChainId, 16);
          } catch (chainIdError) {
            console.error('Failed to get chain ID:', chainIdError);
            // Default to BNB chain if all else fails
            chainId = CHAINS.BNB.id;
          }
        }

        // For reconnection, just connect without enforcing chain
        // For first-time connection, don't force network switch immediately
        // Let the user connect first, then they can switch networks via the UI
        if (!isReconnection && chainId !== CHAINS.BNB.id && chainId !== CHAINS.RONIN.id) {
          // Only switch if they're on an unsupported network
          // If they're on Ronin or BNB, let them use whatever they have
          try {
            await window.ethereum.request({
              method: 'wallet_switchEthereumChain',
              params: [{ chainId: `0x${CHAINS.BNB.id.toString(16)}` }],
            });
            chainId = CHAINS.BNB.id;
          } catch (switchError) {
            // If switch fails, continue with current network
            console.warn('Failed to switch to BNB chain during connection:', switchError);
          }
        }
      }

      dispatch({
        type: WALLET_ACTIONS.CONNECT_SUCCESS,
        payload: {
          address,
          chainId,
          provider,
          signer,
          walletType,
        },
      });

      // Save connection to localStorage
      localStorage.setItem(
        STORAGE_KEYS.WALLET_CONNECTION,
        JSON.stringify({ walletType, address })
      );

      if (showToast) {
        toast.success(`${walletType === WALLET_TYPES.RONIN ? 'Ronin' : 'MetaMask'} connected successfully`);
      }

    } catch (error) {
      console.error('Wallet connection error:', error);
      dispatch({
        type: WALLET_ACTIONS.SET_ERROR,
        payload: error.message,
      });
      
      if (showToast) {
        toast.error(error.message);
      }
    }
  };

  const disconnect = () => {
    dispatch({ type: WALLET_ACTIONS.DISCONNECT });
    localStorage.removeItem(STORAGE_KEYS.WALLET_CONNECTION);
    toast.success('Wallet disconnected');
  };

  const switchChain = async (targetChainId) => {
    if (!state.provider) return;

    try {
      const currentChainId = state.chainId;
      
      if (currentChainId === targetChainId) {
        return; // Already on the correct chain
      }

      const targetChain = Object.values(CHAINS).find(chain => chain.id === targetChainId);
      if (!targetChain) {
        throw new Error('Unsupported chain');
      }

      // Try to switch chain with current wallet first
      const wallet = state.walletType === WALLET_TYPES.RONIN ? window.ronin : window.ethereum;
      
      try {
        // Try to switch to the target chain
      await wallet.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: `0x${targetChainId.toString(16)}` }],
      });
      } catch (switchError) {
        // If chain is not added to wallet, try to add it
        if (switchError.code === 4902) {
          try {
            await wallet.request({
              method: 'wallet_addEthereumChain',
              params: [{
                chainId: `0x${targetChainId.toString(16)}`,
                chainName: targetChain.name,
                nativeCurrency: targetChain.nativeCurrency,
                rpcUrls: [targetChain.rpcUrl],
                blockExplorerUrls: [targetChain.explorerUrl],
              }],
            });
          } catch (addError) {
            // If adding fails and we're trying to access Ronin, suggest using Ronin Wallet
            if (targetChainId === CHAINS.RONIN.id && state.walletType !== WALLET_TYPES.RONIN) {
              // Check if Ronin Wallet is available
              if (window.ronin) {
                toast.error('Ronin chain not supported in MetaMask. Switching to Ronin Wallet...');
                await connectWallet(WALLET_TYPES.RONIN);
                return;
              } else {
                throw new Error('Ronin chain requires Ronin Wallet. Please install Ronin Wallet extension or use MetaMask with Ronin RPC manually added.');
              }
            }
            throw addError;
          }
        } else {
          // If it's a different error and we're targeting Ronin, suggest Ronin Wallet
          if (targetChainId === CHAINS.RONIN.id && state.walletType !== WALLET_TYPES.RONIN) {
            if (window.ronin) {
              toast.error('Switching to Ronin Wallet for better Ronin chain support...');
              await connectWallet(WALLET_TYPES.RONIN);
              return;
            } else {
              throw new Error('For best Ronin chain experience, please install Ronin Wallet extension.');
            }
          }
          throw switchError;
        }
      }

    } catch (error) {
      console.error('Chain switch error:', error);
      const targetChain = Object.values(CHAINS).find(chain => chain.id === targetChainId);
      toast.error(error.message || `Failed to switch to ${targetChain?.name || 'target chain'}`);
    }
  };

  const updateBalance = async () => {
    if (!state.address || !state.provider) return;

    try {
      // Get native token balance
      const nativeBalance = await state.provider.getBalance(state.address);
      const nativeBalanceFormatted = ethers.utils.formatEther(nativeBalance);

      // Get PFVS token balance
      let pfvsBalance = '0';
      const tokenAddress = state.chainId === CHAINS.RONIN.id 
        ? CONTRACTS.RONIN.PFVS_TOKEN 
        : CONTRACTS.BNB.PFVS_TOKEN;

      if (tokenAddress && tokenAddress !== '0x0000000000000000000000000000000000000000') {
        const tokenContract = new ethers.Contract(
          tokenAddress,
          ['function balanceOf(address) view returns (uint256)'],
          state.provider
        );
        
        const balance = await tokenContract.balanceOf(state.address);
        pfvsBalance = ethers.utils.formatEther(balance);
      }

      dispatch({
        type: WALLET_ACTIONS.UPDATE_BALANCE,
        payload: {
          native: nativeBalanceFormatted,
          pfvs: pfvsBalance,
        },
      });

    } catch (error) {
      console.error('Error updating balance:', error);
    }
  };

  // Update balance when address or chain changes
  useEffect(() => {
    if (state.address && state.provider) {
      updateBalance();
    }
  }, [state.address, state.chainId]);

  // Auto-refresh balance at configured interval
  useEffect(() => {
    if (!state.address || !state.provider) return;

    const balanceRefreshInterval = setInterval(() => {
      updateBalance();
    }, REFRESH_INTERVALS.BALANCE);

    return () => clearInterval(balanceRefreshInterval);
  }, [state.address, state.provider, updateBalance]);

  const getChainInfo = (chainId) => {
    return Object.values(CHAINS).find(chain => chain.id === chainId);
  };

  const isCorrectChain = (targetChainId) => {
    return state.chainId === targetChainId;
  };

  const value = {
    // State
    ...state,
    
    // Actions
    connectWallet,
    disconnect,
    switchChain,
    updateBalance,
    
    // Utilities
    getChainInfo,
    isCorrectChain,
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
}

// Custom hook to use wallet context
export function useWallet() {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
}

export default WalletContext; 